// Shared formatting + color helpers for the redesigned dashboard.

export function money(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

/**
 * A precise dollar amount, for per-unit costs.
 *
 * ALWAYS DOLLARS, NEVER CENTS. An earlier version rendered anything under a
 * dollar as cents, so a Cost column read "$1.66" on one row and "18.5¢" on the
 * next. Both are correct and the pair is misleading at a glance: the eye
 * compares 1.66 with 18.5 and reads the second as an order of magnitude larger
 * when it is a tenth. One unit per column, and the symbol carries no
 * information the reader has to decode.
 *
 * Precision adapts so a real cost is never rounded to "$0.00" — that reads as
 * free, which is a different claim from cheap, and this dashboard's whole
 * business is keeping "zero" apart from "nearly zero" and from "unknown".
 * Unknown is the em dash, and only null earns it.
 */
export function usd(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  if (n === 0) return '$0.00';
  const abs = Math.abs(n);
  const dp = abs >= 0.1 ? 2 : abs >= 0.01 ? 3 : 4;
  return `$${n.toFixed(dp)}`;
}

export const fnum = (n: number | string | null | undefined): string =>
  n === null || n === undefined ? '—' : typeof n === 'number' ? n.toLocaleString() : n;

// `now` is injectable so a caller that has already fixed an instant can pass it
// rather than letting this read the clock a second time. lib/nodes.ts judges a
// sighting's age against a threshold and then renders it through here; with two
// separate clock reads the number shown and the number judged were not quite the
// same measurement, which is exactly the kind of gap that makes a boundary case
// impossible to pin down.
export function timeAgo(d: string | number | Date, now: number = Date.now()): string {
  const s = Math.floor((now - new Date(d).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86_400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86_400)}d`;
}

export const hhmmss = (d: string | number | Date): string =>
  new Date(d).toLocaleTimeString([], { hour12: false });

// Hex/var color for SVG strokes & inline styles (status badges use tailwind classes elsewhere).
const HEX: Record<string, string> = {
  up: '#22c55e',
  online: '#22c55e',
  healthy: '#22c55e',
  ONLINE: '#22c55e',
  completed: '#22c55e',
  COMPLETED: '#22c55e',
  // judge-assignment outcomes
  UPDATED: '#22c55e',
  UNCHANGED: '#22c55e',
  NEEDS_REVIEW: '#f59e0b',
  SKIPPED_PLACEHOLDER: 'rgb(var(--accent))',
  ERROR: '#ef4444',
  degraded: '#f59e0b',
  DEGRADED: '#f59e0b',
  shadow: '#f59e0b',
  SHADOW: '#f59e0b',
  in_progress: '#f59e0b',
  IN_PROGRESS: '#f59e0b',
  WARNING: '#f59e0b',
  down: '#ef4444',
  offline: '#ef4444',
  OFFLINE: '#ef4444',
  failed: '#ef4444',
  FAILED: '#ef4444',
  CRITICAL: '#ef4444',
  error: '#ef4444',
  external: 'rgb(var(--accent))',
  pending: 'rgb(var(--accent))',
  PENDING: 'rgb(var(--accent))',
  INFO: 'rgb(var(--accent))',
};
export const colorHex = (status: string): string => HEX[status] ?? 'rgb(var(--text-muted))';

export type PillTone = 'green' | 'amber' | 'red' | 'accent' | 'muted';
export function pillTone(status: string): PillTone {
  const c = colorHex(status);
  if (c === '#22c55e') return 'green';
  if (c === '#f59e0b') return 'amber';
  if (c === '#ef4444') return 'red';
  if (c === 'rgb(var(--accent))') return 'accent';
  return 'muted';
}

export type Sev = 'critical' | 'error' | 'warning' | 'info' | string;
export const sevHex = (s: Sev): string =>
  s === 'critical' || s === 'error'
    ? '#ef4444'
    : s === 'warning'
      ? '#f59e0b'
      : 'rgb(var(--accent))';
export const sevAbbr = (s: Sev): string =>
  s === 'critical' || s === 'error' ? 'ERR' : s === 'warning' ? 'WRN' : 'INF';
